<script setup lang="ts">
import DbButton from "@/components/DB-Components/DbButton.vue";
import DbInput from "@/components/DB-Components/DbInput.vue";
import DbPasswordInput from "@/components/DB-Components/DbPasswordInput.vue";
import { UserData } from "@/data/interfaces/UserDataInterface";
import { reAuthorize } from "@/services/auth/re-auth";
import store from "@/store";
import router from "@/router";
import { computed, onMounted, ref } from "vue";
import {
  isFieldRequired,
  isFieldValid,
  validationMessage,
} from "@/utils/users/methods";
import AppModal from "@/components/AppModal.vue";

const user = ref({
  current_password: "",
  password: "",
  password_confirmation: "",
});

const form = ref({
  errors: {},
  processing: false,
  successful: false,
});

onMounted(() => {
  reAuthorize(store).then((res) => {
    store
      .dispatch("UserStore/getUser", store.state.user.data.user_id)
      .then((response) => {
        user.value = {
          ...(store.state.UserStore.user as UserData),
          ...user.value,
        };
      });
  });
});

const update = async () => {
  form.value.successful = false;
  form.value.processing = true;
  if (!isFormValid.value) {
    return;
  }
  await store
    .dispatch("UserStore/editUser", user.value)
    .then((res) => {
      form.value.successful = true;
    })
    .catch((error) => {
      form.value.successful = false;
      const data = error?.response?.data;
      const errorStatus = data?.httpCode;
      if (errorStatus == 403) {
        form.value.errors = data.httpMessage;
      }
    });
};

const isFormValid = computed(() => {
  let isFilled = true;

  if (form.value.processing == false) {
    const isFilled =
      (Object.keys(user.value) as Array<keyof typeof user.value>).filter(
        (fieldName) =>
          isFieldRequired(fieldName) &&
          (user.value[fieldName] == null || user.value[fieldName]?.length === 0)
      ).length > 0
        ? false
        : true;

    return isFilled;
  }

  return Object.keys(user.value).every((fieldName) =>
    isFieldValid(fieldName as any, user.value)
  );
});

const close = () => {
  router.push("/profile");
};

const clearErrors = () => {
  form.value.errors = {};
};
</script>
<template>
  <div id="profile">
    <div class="flex flex-col p-2 mx-2">
      <div class="w-full flex flex-col mb-3 items-center">
        <div class="flex w-full flex-col">
          <p class="self-end text-sm">{{ $t("account.required") }}</p>
          <p class="self-start text-sm">
            {{ $t("account.confirm_current_password") }}
          </p>
        </div>
        <DbPasswordInput
          v-model="user.current_password"
          :placeholder="$t('account.current_password') + '*'"
          :label="$t('account.current_password') + '*'"
          variant="outline"
          @input="clearErrors"
          class="w-full hydrated"
          name="password"
        />
        <span
          v-if="form.errors.hasOwnProperty('current_password')"
          class="text-red-500 flex justify-center mt-1"
        >
          {{ $t(`account.validation.current_password_wrong`) }}
        </span>
      </div>
      <hr class="mb-3" />
      <div class="flex w-full flex-col">
        <p class="self-start text-sm">
          {{ $t("account.confirm_new_password") }}
        </p>
      </div>
      <div
        class="w-full flex flex-col items-center"
        :class="{
          'pb-6': !form.processing || isFieldValid('password', user),
          'pb-0': !isFieldValid('password', user) && form.processing,
        }"
      >
        <DbPasswordInput
          v-model="user.password"
          :placeholder="$t('account.password') + '*'"
          :label="$t('account.password') + '*'"
          :ariainvalid="
            !isFieldValid('password', user) && form.processing ? 'true' : ''
          "
          variant="outline"
          class="w-full hydrated"
          name="password"
        />
        <span
          v-if="!isFieldValid('password', user) && form.processing"
          class="text-red-500"
        >
          {{ $t("account.validation.password") }}
        </span>
      </div>
      <div
        class="w-full flex flex-col items-center"
        :class="{
          'pb-6':
            !form.processing || isFieldValid('password_confirmation', user),
          'pb-0':
            !isFieldValid('password_confirmation', user) && form.processing,
        }"
      >
        <DbPasswordInput
          v-model="user.password_confirmation"
          :placeholder="$t('account.password_confirmation') + '*'"
          :label="$t('account.password_confirmation') + '*'"
          :ariainvalid="
            !isFieldValid('password_confirmation', user) && form.processing
              ? 'true'
              : ''
          "
          variant="outline"
          class="w-full hydrated"
          name="password_confirmation"
        />
        <span
          v-if="!isFieldValid('password_confirmation', user) && form.processing"
          class="text-red-500"
        >
          {{ $t("account.validation.password_confirmation") }}
        </span>
      </div>

      <div class="flex flex-col gap-3">
        <div class="w-full flex items-center justify-center">
          <DbButton
            @click="() => router.push('/profile')"
            variant="secondary-solid"
            >{{ $t("account.cancel") }}</DbButton
          >
        </div>
        <div class="w-full flex items-center justify-center">
          <DbButton
            @click="update"
            :disabled="!isFormValid"
            variant="primary"
            >{{ $t("account.save") }}</DbButton
          >
        </div>
      </div>
    </div>
  </div>

  <AppModal
    id="reset-password-modal"
    :visible="form.successful"
    modal-size="md"
    modalType="accept"
    cancelText="account.ok"
    @hide-modal="close"
  >
    <!-- Modal body -->
    <div class="p-6 flex flex-col justify-center w-full">
      <h2>********</h2>
      <h2 class="font-bold">
        {{ $t("account.password_changed") }}
      </h2>
    </div>
  </AppModal>
</template>
<style>
#profile
  .elm-input.sc-db-input:not(:placeholder-shown):not(
    :has(+ label[data-label-hidden="true"])
  ).sc-db-input {
  padding-top: 1rem !important;
}
</style>
